import { api } from '@/lib/axios'
import { mitStand } from '@/lib/speichern'
import type { ExportRequest } from '@/lib/export-request'
import type {
  BomFilter,
  BomImportRowState,
  BomImportTarget,
  MaterialCertificateType,
  ProcurementApprovalMode,
  ProcurementSource,
  ProcurementStatus,
} from '@symphenia/shared-types'

/** AP-35 Phase G — Stückliste, Import, Einkaufs-Cockpit (Detailblatt 5). */

export interface BomApproval {
  id: string
  at: string
  byName: string | null
}

/**
 * AP-35 Nachtrag 17 (Detailblatt 5 5.3b a) — was die Lieferanten auf eine offene oder angefragte Zeile angeboten
 * haben. Die beiden Preise fehlen ohne `projects.price.view`.
 */
export interface BomOfferMark {
  state: 'OFFER' | 'APPLIED' | 'REQUESTED'
  /** Angebote mit Preis */
  count: number
  best: {
    documentLineId: string
    documentId: string
    documentNumber: string
    supplierName: string
    /** Nettopreis je Einheit der Zeile */
    bestNetPrice?: number
    /** Nettopreis je Einheit des Angebots */
    bestOfferPrice?: number | null
    offerUnitCode: string | null
    conversion: 'KEINE' | 'IN_KG' | 'ZURUECK'
  } | null
  appliedSupplierName: string | null
  readCount: number
  readSupplierName: string | null
  readDocumentId: string | null
  notOfferedBy: string[]
  requestedFrom: string[]
}

/** AP-35 Nachtrag 17 (5.3b d) — was der Lieferant zu einer bestellten Zeile bestätigt hat. */
export interface BomConfirmationMark {
  state: 'MISSING' | 'READ' | 'CONFIRMED' | 'DEVIATION'
  documentId: string
  documentNumber: string | null
  supplierName: string
  /** AB fehlt seit — Tage ab Versand, sonst Festschreiben */
  sinceDays: number | null
  deliveryAt: string | null
  wishedAt: string | null
  lateDays: number | null
  /** Liefertermin der Position, wenn der bestätigte Termin danach liegt */
  positionDueAt: string | null
  noDate: boolean
  quantityText: string | null
  price: 'HIGHER' | 'LOWER' | null
  externalNumber: string | null
  read: boolean
}

export interface BomLine {
  id: string
  projectId: string
  positionId: string
  sortOrder: number
  source: 'ARTICLE' | 'FREE' | 'CAD'
  importBatchId: string | null
  article: { id: string; articleNumber: string; name: string } | null
  name: string
  longText: string | null
  material: string | null
  category: { id: string; name: string; isAssemblyAccessory: boolean; countsWeight: boolean } | null
  grouping: string | null
  quantity: number
  unit: { id: string; code: string; decimals: number } | null
  altQuantity: number | null
  altUnit: { id: string; code: string } | null
  /** AP-37 N3-8: gesetzt = in kg umgerechnet, kg je Alternativeinheit. */
  altWeightPerUnit: number | null
  quantityBasis: 'TOTAL' | 'PER_UNIT'
  requirement: number
  weightPerUnit: number | null
  totalWeight: number | null
  orderSuggestion: number
  packSize: number | null
  requiresCertificate: boolean
  certificateType: MaterialCertificateType | null
  certificateState: 'ok' | 'missing' | 'insufficient' | 'none'
  certificates: { id: string; number: string; type: MaterialCertificateType; heatNumber: string | null; isActive: boolean }[]
  galvanizingSuitabilityRequired: boolean
  internalNote: string | null
  procurementSource: ProcurementSource
  supplier: { id: string; name: string } | null
  supplierArticleNumber: string | null
  purchasePrice?: number | null
  priceUnit: number
  discountPercent?: number | null
  currency: string | null
  purchaseTotal?: number | null
  deliveryDueAt: string | null
  originalDeliveryDueAt: string | null
  deliveryNote: string | null
  procurementStatus: ProcurementStatus
  inquiryDocument: { id: string; number: string | null } | null
  orderDocument: { id: string; number: string | null } | null
  orderedQuantity: number | null
  requirementIncrease: number | null
  receivedAt: string | null
  approvals: { inquiry: BomApproval | null; order: BomApproval | null }
  inDrafts: string[]
  /** AP-39 10.4, AP-35 N17-1: Angebote der Lieferanten zur Zeile; `null` ohne Anfrage. */
  offer?: BomOfferMark | null
  /** AP-35 N17-10: Auftragsbestätigung einer bestellten Zeile; `null`, solange nicht bestellt. */
  confirmation?: BomConfirmationMark | null
  overdue: boolean
  fileCount: number
  position: { id: string; number: number; name: string; label: string; status: string }
  project: { id: string; projectNumber: string; name: string }
  /** AP-36 8.6: Stand der Zeile — der Zeilenblock schreibt damit gegen Überschreiben. */
  updatedAt?: string
}

export interface BomState {
  lines: BomLine[]
  approvalMode: ProcurementApprovalMode
  /** Mengenbezug der leeren Zeile — Vorgabe aus den Einstellungen. */
  quantityBasisDefault?: 'TOTAL' | 'PER_UNIT'
  /** Menge der Position — für den GP einer Zeile „je Einheit", während getippt wird. */
  positionQuantity?: number
  totals: {
    count: number
    weight: number
    weightUnknown: number
    purchaseTotal?: number
    certificatesMissing: number
    requirementIncreases: number
  }
}

export type BomLineInput = Partial<{
  articleId: string | null
  name: string
  longText: string | null
  material: string | null
  categoryId: string | null
  grouping: string | null
  quantity: number
  unitId: string | null
  altQuantity: number | null
  altUnitId: string | null
  quantityBasis: 'TOTAL' | 'PER_UNIT'
  weightPerUnit: number | null
  requiresCertificate: boolean
  certificateType: string | null
  galvanizingSuitabilityRequired: boolean
  internalNote: string | null
  procurementSource: ProcurementSource
  supplierId: string | null
  supplierArticleNumber: string | null
  purchasePrice: number | null
  priceUnit: number
  discountPercent: number | null
  deliveryDueAt: string | null
  deliveryNote: string | null
}>

export interface BomSuggestion {
  kind: 'ARTICLE' | 'HISTORY'
  id: string
  name: string
  articleNumber?: string
  isPurchaseArticle?: boolean
  unit: { id: string; code: string } | null
  longText?: string | null
  material?: string | null
  weightPerUnit?: number | null
  supplier?: { id: string; name: string } | null
  supplierArticleNumber?: string | null
  purchasePrice?: number | null
  priceUnit?: number
}

export interface BomCategory {
  id: string
  name: string
  sortOrder: number
  isAssemblyAccessory: boolean
  countsWeight: boolean
  isActive: boolean
  /** AP-36: Stand der Zeile. */
  updatedAt?: string
}

export interface ImportMapping {
  id: string
  name: string
  kind: 'CAD' | 'TABLE'
  hasHeader: boolean
  columns: Partial<Record<BomImportTarget, string>>
  unitMap: Record<string, string> | null
}

export interface BomImportRow {
  row: number
  state: BomImportRowState
  reason?: string
  name: string
  quantity: number | null
  unitCode: string | null
  material: string | null
  weightPerUnit: number | null
  articleId: string | null
}

export interface BomImportResult {
  dryRun: boolean
  headers: string[]
  sample: string[][]
  missing: BomImportTarget[]
  rows: BomImportRow[]
  counts: { matched: number; free: number; duplicate: number; invalid: number }
  weight?: number
  toImport?: number
  replaced?: number
  notReplaced?: { id: string; name: string; status: string }[]
  batchId?: string
  mapping: { columns: Partial<Record<BomImportTarget, string>>; unitMap: Record<string, string>; hasHeader: boolean }
}

export interface CertificateCandidate {
  id: string
  number: string
  type: MaterialCertificateType
  issuedAt: string | null
  heatNumber: string | null
  material: string | null
  dimension: string | null
  issuer: string | null
}

const basis = (projectId: string, positionId: string) => `/projects/${projectId}/positions/${positionId}/bom`

export async function fetchBom(projectId: string, positionId: string): Promise<BomState> {
  return (await api.get<{ data: BomState }>(basis(projectId, positionId))).data.data
}

export async function createBomLine(projectId: string, positionId: string, input: BomLineInput): Promise<{ data: BomLine; hints: string[] }> {
  return (await api.post<{ data: BomLine; hints: string[] }>(basis(projectId, positionId), input)).data
}

/** AP-37 N3-8: in kg umrechnen oder zurück — eine Handlung mit Stand wie ein PATCH. */
export async function convertBomLine(lineId: string, to: 'WEIGHT' | 'ORIGINAL', stand?: string): Promise<BomLine> {
  return (await api.post<{ data: BomLine }>(`/projects/bom/${lineId}/convert`, { to }, mitStand(stand))).data.data
}

/** Löschen legt die Zeile in den Papierkorb (AP-35 Nachtrag 6) — die Antwort nennt den Eintrag fürs Rückgängig. */
export async function deleteBomLine(lineId: string): Promise<{ trashId: string; name: string }> {
  return (await api.delete<{ data: { trashId: string; name: string } }>(`/projects/bom/${lineId}`)).data.data
}

export interface BomTrashEntry {
  id: string
  lineId: string
  name: string
  deletedAt: string
  deletedByName: string | null
  expiresAt: string
}

export async function fetchBomTrash(projectId: string, positionId: string): Promise<BomTrashEntry[]> {
  return (await api.get<{ data: BomTrashEntry[] }>(`${basis(projectId, positionId)}/trash`)).data.data
}

export async function restoreBomLine(trashId: string): Promise<BomState> {
  return (await api.post<{ data: BomState }>(`/projects/bom/trash/${trashId}/restore`)).data.data
}

/** Export der Stückliste — derselbe Filter wie im Reiter (`BomFilter` als JSON). */
export function bomExportRequest(projectId: string, positionId: string, params: { format: string; orientation?: string; filter: BomFilter; filterInfo?: string }): ExportRequest {
  const p: Record<string, unknown> = { format: params.format, locale: navigator.language }
  if (params.orientation) p.orientation = params.orientation
  if (Object.keys(params.filter).length) p.filter = JSON.stringify(params.filter)
  if (params.filterInfo) p.filterInfo = params.filterInfo
  return { url: `${basis(projectId, positionId)}/export`, params: p }
}

export async function reorderBom(projectId: string, positionId: string, lineIds: string[]): Promise<BomState> {
  return (await api.post<{ data: BomState }>(`${basis(projectId, positionId)}/reorder`, { lineIds })).data.data
}

export async function pasteIntoBom(projectId: string, positionId: string, itemIds: string[]): Promise<BomState> {
  return (await api.post<{ data: BomState }>(`${basis(projectId, positionId)}/paste`, { itemIds })).data.data
}

export async function receiveBomLines(lineIds: string[], receivedAt?: string) {
  await api.post('/projects/bom/receive', { lineIds, ...(receivedAt ? { receivedAt } : {}) })
}

export async function cancelBomLines(lineIds: string[]) {
  await api.post('/projects/bom/cancel', { lineIds })
}

export async function copyBomLines(lineIds: string[]) {
  await api.post('/projects/bom/copy', { lineIds })
}

export async function approveBomLines(lineIds: string[], kind: 'INQUIRY' | 'ORDER') {
  await api.post('/projects/bom/approve', { lineIds, kind })
}

export async function revokeBomApproval(lineIds: string[], kind: 'INQUIRY' | 'ORDER', reason?: string) {
  await api.post('/projects/bom/approve/revoke', { lineIds, kind, reason })
}

export async function suggestBom(q: string, source: 'ARTICLES' | 'HISTORY', supplierId?: string | null): Promise<BomSuggestion[]> {
  const res = await api.get<{ data: BomSuggestion[] }>('/projects/bom/suggest', {
    params: { q, source, ...(supplierId ? { supplierId } : {}) },
  })
  return res.data.data
}

export async function certificateCandidates(lineId: string, q?: string): Promise<CertificateCandidate[]> {
  return (await api.get<{ data: CertificateCandidate[] }>(`/projects/bom/${lineId}/certificate-candidates`, { params: q ? { q } : {} })).data.data
}

export async function assignCertificate(certificateId: string, lineIds: string[]): Promise<string[]> {
  return (await api.post<{ hints: string[] }>(`/projects/bom/certificates/${certificateId}`, { lineIds })).data.hints ?? []
}

export async function unassignCertificate(lineId: string, certificateId: string) {
  await api.delete(`/projects/bom/${lineId}/certificates/${certificateId}`)
}

export async function linesForCertificate(certificateId: string, documentId?: string): Promise<(BomLine & { reason: 'ORDER' | 'MATERIAL' | 'SUPPLIER' })[]> {
  return (
    await api.get<{ data: (BomLine & { reason: 'ORDER' | 'MATERIAL' | 'SUPPLIER' })[] }>('/projects/bom/certificate-suggestions', {
      params: { certificateId, ...(documentId ? { documentId } : {}) },
    })
  ).data.data
}

export async function importBom(
  projectId: string,
  positionId: string,
  file: File,
  felder: { source: 'CAD' | 'TABLE'; mappingId?: string; columns?: object; unitMap?: object; hasHeader?: boolean; mode?: 'APPEND' | 'REPLACE'; dryRun?: boolean; rows?: number[]; saveMappingAs?: string },
): Promise<BomImportResult> {
  const form = new FormData()
  form.append('file', file)
  form.append('source', felder.source)
  if (felder.mappingId) form.append('mappingId', felder.mappingId)
  if (felder.columns) form.append('columns', JSON.stringify(felder.columns))
  if (felder.unitMap) form.append('unitMap', JSON.stringify(felder.unitMap))
  if (felder.hasHeader !== undefined) form.append('hasHeader', String(felder.hasHeader))
  if (felder.mode) form.append('mode', felder.mode)
  if (felder.dryRun) form.append('dryRun', 'true')
  if (felder.rows) form.append('rows', JSON.stringify(felder.rows))
  if (felder.saveMappingAs) form.append('saveMappingAs', felder.saveMappingAs)
  return (await api.post<{ data: BomImportResult }>(`${basis(projectId, positionId)}/import`, form)).data.data
}

export async function undoImport(batchId: string) {
  await api.post(`/projects/bom/import/${batchId}/undo`)
}

export async function fetchImportMappings(): Promise<ImportMapping[]> {
  return (await api.get<{ data: ImportMapping[] }>('/projects/import-mappings')).data.data
}

export async function deleteImportMapping(id: string) {
  await api.delete(`/projects/import-mappings/${id}`)
}

export async function fetchBomCategories(): Promise<BomCategory[]> {
  return (await api.get<{ data: BomCategory[] }>('/projects/bom-categories')).data.data
}

export async function saveBomCategory(input: Partial<BomCategory>, id?: string): Promise<BomCategory> {
  return id
    ? (await api.patch<{ data: BomCategory }>(`/projects/bom-categories/${id}`, input)).data.data
    : (await api.post<{ data: BomCategory }>('/projects/bom-categories', input)).data.data
}

export async function deleteBomCategory(id: string) {
  await api.delete(`/projects/bom-categories/${id}`)
}

// ── Einkaufs-Cockpit ───────────────────────────────────────────────────────

export interface PurchasingFilter {
  tab?: 'open' | 'ordered' | 'overdue'
  search?: string
  supplierId?: string
  projectId?: string
  categoryId?: string
  /** „enthält" — Projektnummer oder -bezeichnung. */
  projectSearch?: string
  /** „enthält" — Positionsbezeichnung; eine Zahl trifft auch die Positionsnummer. */
  positionSearch?: string
  /** „enthält" — Name des Kunden am Projekt. */
  clientName?: string
  dueTo?: string
  approval?: 'missing' | 'granted'
  requiresCertificate?: boolean
  includePlanned?: boolean
  /** AP-35 N17-12: Stand der Auftragsbestätigung bestellter Zeilen */
  confirmation?: PurchasingConfirmationFilter
}

export type PurchasingConfirmationFilter = 'MISSING' | 'DEVIATION' | 'CONFIRMED'

export type PurchasingLine = BomLine & { orderApprovalMissing: boolean }

export interface PurchasingResult {
  data: PurchasingLine[]
  approvalMode: ProcurementApprovalMode
  canCreateDocuments: boolean
  /** N17-12: über den bestellten Zeilen des Reiters — vor dem Filter „Auftragsbestätigung" gezählt */
  counts?: { ordered: number; missing: number; deviation: number }
}

export async function fetchPurchasing(filter: PurchasingFilter): Promise<PurchasingResult> {
  const res = await api.get<PurchasingResult>('/purchasing', { params: filter })
  return res.data
}

export function purchasingExportRequest(filter: PurchasingFilter & Record<string, unknown>, columns: { key: string; label: string }[]): ExportRequest {
  const params: Record<string, unknown> = { ...filter, columns: JSON.stringify(columns), locale: navigator.language }
  for (const k of Object.keys(params)) if (params[k] === undefined || params[k] === '') delete params[k]
  return { url: '/purchasing/export', params }
}

export async function assignSupplier(lineIds: string[], supplierId: string) {
  return (await api.post<{ data: { updated: number; skipped: string[] } }>('/purchasing/assign-supplier', { lineIds, supplierId })).data.data
}

export async function setDue(lineIds: string[], deliveryDueAt: string) {
  await api.post('/purchasing/set-due', { lineIds, deliveryDueAt })
}

export type PurchaseDocumentKind = 'INQUIRY' | 'PURCHASE_ORDER'

/** Nachtrag 13: vorgeschlagener Empfänger — an der Stücklistenzeile oder im Artikelstamm. */
export interface PurchaseSuggestion {
  id: string
  name: string
  clientNumber: string
  /** an wie vielen gewählten Zeilen er als Lieferant steht */
  lineCount: number
  /** zu wie vielen gewählten Zeilen er im Artikelstamm steht */
  articleCount: number
  matching: number
  preferred: boolean
}

export interface PurchasePreviewDocument {
  supplierId: string
  supplierName: string
  projectId: string | null
  projects: string[]
  lines: {
    lineId: string
    name: string
    material: string | null
    project: string
    position: string
    quantity: number
    unitCode: string | null
    unitPrice?: number
    priceUnit?: number
    /** an der Zeile steht ein anderer Lieferant als der Empfänger */
    lineSupplier: string | null
    files: number
    /** AP-35 N17-6: der Preis kommt aus dem Angebot an dieser Preisanfrage */
    offerDocumentNumber?: string | null
    /** N17-7: der Empfänger bietet in dieser Einheit an — erst an der Zeile übernehmen */
    offerUnitHint?: string | null
  }[]
  /** N17-6: „Ihr Zeichen" — das Angebot, wenn alle Angebotspreise aus einer Antwort stammen */
  customerReference?: string | null
  purchaseTotal?: number
  files: { projectFileId: string; fileName: string; sizeBytes: number; line: string; hint: 'TYPE' | 'TOO_MANY' | null }[]
  filesBytes: number
  filesAsLinkHint: boolean
}

export interface CreateDocumentsResult {
  dryRun: boolean
  groups: PurchasePreviewDocument[]
  skipped: { lineId: string; name: string; reason: string; detail?: string }[]
  created: { documentId: string; documentNumber: string | null; supplierName: string; lines: number; skippedFiles: { fileName: string; reason: string }[] }[]
  suggestions: PurchaseSuggestion[]
}

export interface CreateDocumentsParams {
  lineIds: string[]
  kind: PurchaseDocumentKind
  /** Nachtrag 13: gewählte Empfänger — jeder bekommt alle Zeilen; ohne das Feld gilt der Lieferant der Zeile. */
  supplierIds?: string[]
  /** Zeilen, die ein Empfänger nicht bekommt */
  excludedLines?: { supplierId: string; lineId: string }[]
  split?: 'PER_SUPPLIER' | 'PER_SUPPLIER_AND_PROJECT'
  deliveryAddress?: 'NONE' | 'PROJECT_SITE'
  withFiles?: boolean
  excludedFileIds?: string[]
  dryRun?: boolean
}

export async function createPurchaseDocuments(input: CreateDocumentsParams): Promise<CreateDocumentsResult> {
  return (await api.post<{ data: CreateDocumentsResult }>('/purchasing/create-documents', input)).data.data
}

/** Lieferanten für Auswahlfelder — über die Partnerliste (`role=supplier`). */
export async function searchSuppliers(q: string): Promise<{ id: string; name: string }[]> {
  const res = await api.get<{ data: { id: string; name: string }[] }>('/clients', { params: { search: q, limit: 15, role: 'supplier' } })
  return res.data.data ?? []
}

export async function fetchUnits(): Promise<{ id: string; code: string; name: string; decimals: number }[]> {
  const res = await api.get<{ data: { id: string; code: string; name: string; decimals: number }[] }>('/projects/lookups/units')
  return res.data.data
}

// ── AP-39 10.4: Angebote im Einkauf — Preisspiegel und Übernahme ──────────────

/** Das Angebot eines Lieferanten zu einer Stücklistenzeile: die Antwort an der Belegzeile seiner Preisanfrage. */
export interface BomOffer {
  documentLineId: string
  documentId: string
  documentNumber: string
  supplierId: string
  supplierName: string
  unitPrice: number | null
  priceUnit: number
  discountPercent: number | null
  /** wie angeboten */
  offerUnitCode: string | null
  offerNetPrice: number | null
  /** Nettopreis je Einheit der Zeile */
  netPrice: number | null
  converted: boolean
  unitMatches: boolean
  conversion: 'KEINE' | 'IN_KG' | 'ZURUECK'
  leadTimeDays: number | null
  notOffered: boolean
  alternative: string | null
  articleNumber: string | null
  answeredAt: string | null
  validUntil: string | null
  applied: boolean
  costNew: number | null
}

export interface OfferComparisonRow {
  lineId: string
  projectId: string
  projectLabel: string
  positionLabel: string
  name: string
  material: string | null
  unitCode: string | null
  quantity: number
  procurementStatus: ProcurementStatus
  updatedAt: string
  current: { purchasePrice: number | null; priceUnit: number; discountPercent: number | null; netPrice: number | null; cost: number | null; supplierName: string | null }
  offers: BomOffer[]
  bestDocumentLineId: string | null
  appliedDocumentLineId: string | null
}

export interface OfferComparison {
  rows: OfferComparisonRow[]
  suppliers: { id: string; name: string; total: number; answered: number; notOffered: number; missing: number }[]
}

export async function fetchOfferComparison(filter: { projectId?: string; positionId?: string; supplierId?: string; open?: boolean }): Promise<OfferComparison> {
  return (await api.get<{ data: OfferComparison }>('/purchasing/comparison', { params: filter })).data.data
}

export interface ApplyOffersResult {
  dryRun: boolean
  applied: number
  lines: {
    lineId: string
    name: string
    projectLabel: string
    positionLabel: string
    supplierName: string
    documentNumber: string
    costBefore: number | null
    costAfter: number | null
    netPriceBefore: number | null
    netPriceAfter: number | null
    unitCode: string | null
    conversion: 'KEINE' | 'IN_KG' | 'ZURUECK'
    deliveryDueAt: string | null
  }[]
  skipped: { lineId: string; name: string; reason: 'ORDERED' | 'NO_PRICE' | 'NO_WEIGHT' | 'UNIT' | 'GONE' }[]
  totalBefore: number
  totalAfter: number
}

export async function applyOffers(input: {
  choices: { lineId: string; documentLineId: string }[]
  stands?: { lineId: string; updatedAt: string }[]
  dryRun?: boolean
}): Promise<ApplyOffersResult> {
  return (await api.post<{ data: ApplyOffersResult }>('/purchasing/offers/apply', input)).data.data
}
